import React, { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  BoxV2 as Box,
  Flex,
  TextInput,
  DropdownV2 as Dropdown,
  Tooltip,
} from 'portal-commons';
import { SelectItem } from '@/utils/types';
import { ValidationRule } from '@/apis/core/types';
import TwoColumnGrid from './TwoColumnGrid';
import StepFooter from './StepFooter';
import useCspDetailsControllers from '../hooks/useCspDetailsControllers';
import { CspRegistrationFormData } from '../types';
import { ENTITY_TYPE_OPTIONS } from '../constants';
import { ContentBlockHeader } from '@/components';
import { faBuildingCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import Divider from './Divider';

interface StepCspDetailsProps {
  onBack: () => void;
  countryOptions: SelectItem[];
  stockExchangeOptions: SelectItem[];
  usStateOptions: SelectItem[];
  validationRules: ValidationRule[];
}

const LongLabelInputWrapper: FC = ({ children }) => (
  <Box
    sx={{
      // Fix floating label overflow issue in multiline textField
      '& .MuiInputLabel-root': {
        width: 'calc(100% - 28px)',
      },
      // Fix floating label too long in multiline textField
      '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        fontSize: '13px !important',
        whiteSpace: 'nowrap',
      },
    }}
  >
    {children}
  </Box>
);

const StepCspDetails: FC<StepCspDetailsProps> = ({
  onBack,
  countryOptions,
  stockExchangeOptions,
  usStateOptions,
  validationRules,
}) => {
  const {
    control,
    formState: { isValid, isValidating },
    setValue,
    resetField,
  } = useFormContext<CspRegistrationFormData>();

  const {
    companyName,
    setCompanyName,
    validateCompanyName,
    companyNameError,
    displayName,
    setDisplayName,
    validateDisplayName,
    displayNameError,
    countryOfRegistration,
    setCountryOfRegistration,
    countryError,
    entityTypeVal,
    setEntityType,
    entityTypeError,
    ein,
    setEin,
    validateEin,
    einError,
    street,
    setStreet,
    validateStreet,
    streetError,
    city,
    setCity,
    validateCity,
    cityError,
    state,
    setState,
    validateState,
    stateError,
    postalCode,
    setPostalCode,
    validatePostalCode,
    postalCodeError,
    website,
    setWebsite,
    validateWebsite,
    websiteError,
    stockSymbol,
    setStockSymbol,
    validateStockSymbol,
    stockSymbolError,
    stockSymbolDisabled,
    stockExchange,
    setStockExchange,
    stockExchangeError,
    stockExchangeDisabled,
    snowshoeAnswer,
    setSnowshoeAnswer,
    validateSnowshoeAnswer,
    snowshoeAnswerError,
    atoAnswer,
    setAtoAnswer,
    validateAtoAnswer,
    atoAnswerError,
    isUS,
    einLabel,
    postalCodeLabel,
  } = useCspDetailsControllers(control, validationRules);

  useEffect(() => {
    if (!website) {
      // If website is empty but has an error, reset the field states completely first
      if (websiteError) {
        resetField('details.website');
      }

      setValue('details.website', 'https://', {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, []);

  const isStockFieldRequired =
    !entityTypeVal || entityTypeVal === 'PUBLIC_PROFIT';

  const isNextDisabled = isValidating || !isValid;

  // Sync logic: updates CSP DBA Name automatically when CSP Legal Name changes
  const handleCompanyNameChange = (val: string) => {
    setCompanyName(val);
    setDisplayName(val);
  };

  return (
    <Flex sx={{ flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 24, pb: 24 }}
      >
        <ContentBlockHeader icon={faBuildingCircleCheck} title="CSP Details" />
        <Divider sx={{ my: 24 }} />
        <TwoColumnGrid sx={{ rowGap: 32, gridTemplateRows: '40px' }}>
          {/* Row 1: companyName | displayName */}
          <Flex sx={{ position: 'relative' }}>
            <Box sx={{ flex: 1, mr: 16 }}>
              <TextInput
                data-testid="cspBasicDetailsNewCompanyNameInput"
                label="CSP Legal Name"
                required
                value={companyName}
                onChange={handleCompanyNameChange}
                onBlur={validateCompanyName}
                error={!!companyNameError}
                note={companyNameError?.message}
              />
            </Box>
            <Tooltip
              title="Type in the legally registered company name"
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                marginTop: '12px',
              }}
            />
          </Flex>
          <Flex sx={{ position: 'relative' }}>
            <Box sx={{ flex: 1, mr: 16 }}>
              <TextInput
                data-testid="cspBasicDetailsNewDisplayNameInput"
                label="CSP DBA Name"
                value={displayName}
                onChange={setDisplayName}
                onBlur={validateDisplayName}
                error={!!displayNameError}
                note={
                  displayNameError?.message ||
                  'Only required if different from your CSP Legal Name.'
                }
              />
            </Box>
            <Tooltip
              title="Type in the 'Doing Business As' (DBA) or trade name of the company"
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                marginTop: '12px',
              }}
            />
          </Flex>

          {/* Row 2: entityTypeVal | countryOfRegistration */}
          <Dropdown
            data-testid="cspBasicDetailsNewEntityTypeSelect"
            label="What type of legal form is the organization?"
            required
            options={ENTITY_TYPE_OPTIONS}
            value={entityTypeVal}
            onChange={setEntityType}
            error={!!entityTypeError}
            helperText={entityTypeError?.message}
          />
          <Flex sx={{ position: 'relative' }}>
            <Box sx={{ flex: 1, mr: 16 }}>
              <Dropdown
                data-testid="cspBasicDetailsNewCountrySelect"
                label="Country of Registration"
                required
                options={countryOptions}
                value={countryOfRegistration}
                onChange={setCountryOfRegistration}
                error={!!countryError}
                helperText={countryError?.message}
              />
            </Box>
            <Tooltip
              title="Select the country in which the company is located"
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                marginTop: '12px',
              }}
            />
          </Flex>

          {/* Row 3: stockSymbol | stockExchange */}
          {isStockFieldRequired && (
            <TextInput
              data-testid="cspBasicDetailsStockSymbolInput"
              label="Stock Symbol"
              required={isStockFieldRequired}
              disabled={stockSymbolDisabled}
              value={stockSymbol}
              onChange={setStockSymbol}
              onBlur={validateStockSymbol}
              error={!!stockSymbolError}
              note={stockSymbolError?.message}
            />
          )}
          {isStockFieldRequired && (
            <Dropdown
              data-testid="cspBasicDetailsStockExchangeSelect"
              label="Stock Exchange"
              required={isStockFieldRequired}
              disabled={stockExchangeDisabled}
              options={stockExchangeOptions}
              value={stockExchange}
              onChange={setStockExchange}
              error={!!stockExchangeError}
              helperText={stockExchangeError?.message}
            />
          )}

          {/* Row 4: ein | Box (placeholder) */}
          <Flex sx={{ position: 'relative' }}>
            <Box sx={{ flex: 1, mr: isUS ? 16 : 0 }}>
              <TextInput
                data-testid="cspBasicDetailsNewEinInput"
                label={einLabel}
                required
                value={ein}
                onChange={setEin}
                onBlur={validateEin}
                error={!!einError}
                note={einError?.message}
              />
            </Box>
            {isUS && (
              <Tooltip
                title="The Employer Identification Number (EIN) is a nine-digit number assigned by the IRS. It's used to identify the tax accounts of employers and certain others who have no employees. The IRS uses the number to identify taxpayers who are required to file varioius business tax returns. EINs are used by employers, sole proprietors, corporations, partnerships, non-profit organizations, trusts, estates of decendants, government agencies, certain individuals, and other business entities."
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  marginTop: '12px',
                }}
              />
            )}
          </Flex>
          <Box />

          {/* Row 5: street | city */}
          <TextInput
            data-testid="cspBasicDetailsNewStreetInput"
            label="Address"
            required
            value={street}
            onChange={setStreet}
            onBlur={validateStreet}
            error={!!streetError}
            note={streetError?.message}
          />
          <TextInput
            data-testid="cspBasicDetailsNewCityInput"
            label="City"
            required
            value={city}
            onChange={setCity}
            onBlur={validateCity}
            error={!!cityError}
            note={cityError?.message}
          />

          {/* Row 6: state | postalCode */}
          {isUS ? (
            <Dropdown
              data-testid="cspBasicDetailsNewCountryStateInput"
              label="State"
              required
              options={usStateOptions}
              value={state}
              onChange={setState}
              error={!!stateError}
              helperText={stateError?.message}
            />
          ) : (
            <TextInput
              data-testid="cspBasicDetailsNewCountryStateInput"
              label="State / Region"
              required
              value={state}
              onChange={setState}
              onBlur={validateState}
              error={!!stateError}
              note={stateError?.message}
            />
          )}
          <TextInput
            data-testid="cspBasicDetailsNewPostalCodeInput"
            label={postalCodeLabel}
            required
            value={postalCode}
            onChange={setPostalCode}
            onBlur={validatePostalCode}
            error={!!postalCodeError}
            note={postalCodeError?.message}
          />

          {/* Row 7: country (read-only) | website */}
          <TextInput
            label="Country"
            readOnly
            value={
              countryOptions.find((opt) => opt.value === countryOfRegistration)
                ?.label || countryOfRegistration
            }
          />
          <TextInput
            data-testid="cspBasicDetailsWebsiteInput"
            label="Website / Online Presence"
            required
            value={website}
            onChange={setWebsite}
            onBlur={validateWebsite}
            error={!!websiteError}
            note={websiteError?.message}
          />

          {/* Row 8: Snowshoe | ATO (Questionnaire) */}
          <LongLabelInputWrapper>
            <TextInput
              data-testid="cspBasicDetailsSnowshoeAnswerInput"
              label="How do you prevent your customers from snowshoeing accounts?"
              required
              multiline
              maxLength={1000}
              minRows={3}
              maxRows={4}
              value={snowshoeAnswer}
              onChange={setSnowshoeAnswer}
              onBlur={validateSnowshoeAnswer}
              error={!!snowshoeAnswerError}
              note={snowshoeAnswerError?.message}
            />
          </LongLabelInputWrapper>
          <LongLabelInputWrapper>
            <TextInput
              data-testid="cspBasicDetailsATOAnswerInput"
              label="What systems are in place to protect your customers from account takeover?"
              required
              multiline
              maxLength={1000}
              minRows={3}
              maxRows={4}
              value={atoAnswer}
              onChange={setAtoAnswer}
              onBlur={validateAtoAnswer}
              error={!!atoAnswerError}
              note={atoAnswerError?.message}
            />
          </LongLabelInputWrapper>
        </TwoColumnGrid>
      </Box>

      <StepFooter
        onBack={onBack}
        nextLabel="Next: Contact Details"
        nextDisabled={isNextDisabled}
      />
    </Flex>
  );
};

export default StepCspDetails;
